/**
 * TenantPort design tokens — "Theme D: Brand".
 *
 * Canonical token file for the TenantPort redesign, derived from the brand
 * logo files (`TenantPort assets/Logo Files`): teal #387C88 + deep teal
 * #2C626B (the wordmark ink), with teal-tinted neutrals and geometric-rounded
 * type (Poppins display / DM Sans UI) echoing the All Round Gothic wordmark.
 *
 * This file is pure data — no MUI imports, no functions. The resident app
 * mirrors it at `resident-portal-app/src/shared/theme/tokens.ts`; when a
 * value changes here, change it there too.
 */

const tokens = {
  colors: {
    // Brand
    primary: '#387C88',
    primaryDeep: '#2C626B', // wordmark ink — hover, active, emphasis
    primaryDark: '#1E444B',
    primarySoft: '#E3EFF0', // active pills, soft chips, selected rows
    primaryTint: '#C5DDE0',

    // Ink (teal-tinted text scale)
    ink: '#182F33',
    inkSecondary: '#466064',
    inkMuted: '#5E7579',
    inkFaint: '#9FB0B1',

    // Surfaces
    pageBg: '#F5F8F8',
    surface: '#FFFFFF',
    tableHeadBg: '#EFF5F5',
    hover: '#F1F6F6',

    // Borders
    border: '#DCE4E5',
    borderStrong: '#CFDBDD',

    // Status
    success: { bg: '#E3F4E6', fg: '#1F7A33', main: '#2E8B44' },
    warning: { bg: '#FCF1DC', fg: '#96660A', main: '#B37B0D' },
    error: { bg: '#FBE7EA', fg: '#A4233F', main: '#B3273E', border: '#EBB4BF' },
    info: { bg: '#E3EFF0', fg: '#2C626B', main: '#387C88' },
    neutral: { bg: '#EDF1F1', fg: '#5A6F6F' },

    focusRing: 'rgba(56, 124, 136, 0.30)',
    overlay: 'rgba(24, 47, 51, 0.34)',
  },

  fonts: {
    body: "'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    display: "'Poppins', 'DM Sans', -apple-system, sans-serif",
    mono: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
  },

  radius: {
    sm: 7, // inputs, buttons, small controls
    md: 10, // cards, panels, tables
    lg: 14, // dialogs, large surfaces
    pill: 99,
  },

  shadows: {
    card: '0px 1px 2px rgba(24, 47, 51, 0.05), 0px 3px 10px rgba(24, 47, 51, 0.05)',
    overlay: '0px 10px 34px rgba(24, 47, 51, 0.20)',
  },

  layout: {
    sidebarWidth: 220,
    sidebarMiniWidth: 64,
    contentMaxWidth: 1600,
  },

  /**
   * Central status → chip color map. One source of truth for every status
   * chip in the app (lease status, application status, KYC, work orders,
   * ledger, income verification). Keys are lowercase.
   */
  statusColors: {
    // generic
    success: { bg: '#E3F4E6', fg: '#1F7A33' },
    warning: { bg: '#FCF1DC', fg: '#96660A' },
    error: { bg: '#FBE7EA', fg: '#A4233F' },
    info: { bg: '#E3EFF0', fg: '#2C626B' },
    neutral: { bg: '#EDF1F1', fg: '#5A6F6F' },
    // lease / person statuses
    current: { bg: '#E3F4E6', fg: '#1F7A33' },
    pending: { bg: '#FCF1DC', fg: '#96660A' },
    'pending transfer': { bg: '#E3EFF0', fg: '#2C626B' },
    former: { bg: '#FBE7EA', fg: '#A4233F' },
    evicted: { bg: '#FBE7EA', fg: '#A4233F' },
    'under eviction': { bg: '#FCF1DC', fg: '#96660A' },
    // application / verification
    completed: { bg: '#E3F4E6', fg: '#1F7A33' },
    submitted: { bg: '#E3F4E6', fg: '#1F7A33' },
    verified: { bg: '#E3F4E6', fg: '#1F7A33' },
    'in progress': { bg: '#FCF1DC', fg: '#96660A' },
    failed: { bg: '#FBE7EA', fg: '#A4233F' },
    cancelled: { bg: '#FBE7EA', fg: '#A4233F' },
    'not started': { bg: '#EDF1F1', fg: '#5A6F6F' },
  },
};

export default tokens;
